import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import Spinner from 'src/components/library/spinner/Spinner';
import UnderConstruction from 'src/components/under-construction/UnderConstruction';
import { FEATURE_FLAG_KEYS } from 'src/config/featureFlags';
import { syncFeatureFlagsOperation } from 'src/store/feature-flags/feature-flags.operations';
import {
    featureFlagsLoadingSelector,
    isFeatureEnabledSelector,
} from 'src/store/feature-flags/feature-flags.selectors';

const LoadingShell = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100vw;
    height: 100vh;
    background-color: ${({ theme }) => theme?.background?.default || '#ffffff'};
`;

/**
 * Blocks the authenticated portal shell when `enable-under-maintenance-post-login`
 * is enabled for the signed-in LaunchDarkly user context. Runs after Auth0 login
 * so segment targeting (e.g. production test users) can bypass maintenance.
 */
const AppPostLoginMaintenanceGate = ({ children }) => {
    const dispatch = useDispatch();
    const flagsLoading = useSelector(featureFlagsLoadingSelector);
    const isPostLoginMaintenance = useSelector((state) =>
        isFeatureEnabledSelector(state, FEATURE_FLAG_KEYS.ENABLE_UNDER_MAINTENANCE_POST_LOGIN));
    const [hasSyncedAuthenticatedFlags, setHasSyncedAuthenticatedFlags] = useState(false);

    useEffect(() => {
        let isActive = true;

        dispatch(syncFeatureFlagsOperation({ isRefresh: true }))
            .finally(() => {
                if (isActive) {
                    setHasSyncedAuthenticatedFlags(true);
                }
            });

        return () => {
            isActive = false;
        };
    }, [dispatch]);

    if (!hasSyncedAuthenticatedFlags || flagsLoading) {
        return (
            <LoadingShell role="status" aria-live="polite" aria-label="Loading application">
                <Spinner size="large" />
            </LoadingShell>
        );
    }

    if (isPostLoginMaintenance) {
        return <UnderConstruction />;
    }

    return children;
};

AppPostLoginMaintenanceGate.propTypes = {
    children: PropTypes.node.isRequired,
};

export default AppPostLoginMaintenanceGate;
